import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { usePlatformStats } from '@/api/hooks/useSuperadmin';
import { NAV_ITEMS, SUBSCRIPTION_NAV, SUPERADMIN_NAV } from '@/components/layout/nav-config';
import { useSession } from '@/auth/session';
import { cn } from '@/lib/utils';

function NavRow({
  item,
  onNavigate,
  badge,
}: {
  item: (typeof NAV_ITEMS)[number];
  onNavigate?: () => void;
  badge?: number;
}) {
  const { t } = useTranslation();
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-ink-200 transition-colors hover:bg-white/5 hover:text-white',
          isActive && 'bg-white/10 text-white shadow-[inset_2px_0_0_0_theme(colors.jade.400)]',
        )
      }
    >
      <Icon className="h-[18px] w-[18px] shrink-0" />
      <span className="flex-1">{item.labelKey ? t(item.labelKey) : item.label}</span>
      {!!badge && (
        <span className="rounded-full bg-status-low/20 px-1.5 py-0.5 text-xs font-semibold tabular text-status-low">
          {badge}
        </span>
      )}
    </NavLink>
  );
}

export function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const { can, isSuperadmin, pharmacy } = useSession();
  const visible = NAV_ITEMS.filter((item) => !item.permission || can(item.permission));

  // Cheap enough to run in the shell — the same query backs the Overview
  // tab, and React Query dedupes it once the operator opens that screen.
  const { data: stats } = usePlatformStats();

  return (
    <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-4">
      {visible.map((item) => (
        <NavRow key={item.to} item={item} onNavigate={onNavigate} />
      ))}

      {!isSuperadmin && pharmacy && (
        <>
          <div className="my-2 h-px bg-white/10" />
          <NavRow item={SUBSCRIPTION_NAV} onNavigate={onNavigate} />
        </>
      )}

      {isSuperadmin && (
        <>
          <div className="my-2 h-px bg-white/10" />
          <NavRow item={SUPERADMIN_NAV} onNavigate={onNavigate} badge={stats?.totals.pendingClaims} />
        </>
      )}
    </nav>
  );
}